/**
 * Settings · Petty Cash Categories
 *
 * Lets admins curate the list of categories that appear in the petty-cash
 * expense form (web + mobile). Categories are stored in
 * `petty_cash_categories` (soft-deleted) — every petty-cash form pulls
 * from `GET /api/petty-cash/categories`.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save, X, Loader2, Tag } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';

export default function PettyCashCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/petty-cash/categories');
      setItems(Array.isArray(r?.items) ? r.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = newName.trim();
    if (!name) return toast.error('Enter a category name');
    setAdding(true);
    try {
      await apiPost('/petty-cash/categories', { name });
      setNewName('');
      toast.success(`Added "${name}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add');
    } finally { setAdding(false); }
  };

  const startEdit = (it) => { setEditingId(it.id); setEditName(it.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };

  const saveEdit = async (it) => {
    const name = editName.trim();
    if (!name) return toast.error('Name cannot be empty');
    try {
      await apiPut(`/petty-cash/categories/${it.id}`, { name });
      toast.success('Saved');
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.name}" from the list?\n\nExisting expenses already in this category are not affected.`)) return;
    try {
      await apiDel(`/petty-cash/categories/${it.id}`);
      toast.success('Removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <div data-testid="petty-cash-categories-page">
      <PageHeader
        overline="Settings · Petty Cash"
        title="Petty Cash Categories"
        subtitle="Curate the categories users pick from when submitting petty-cash expenses. Used by both the web and mobile forms."
        icon={<Tag size={18} />}
      />

      {/* Add new */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
        <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Add a new category</div>
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder='e.g. "Office Stationery"'
            className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#1F7A6E] focus:ring-1 focus:ring-[#1F7A6E] focus:outline-none"
            data-testid="pc-category-new-input"
          />
          <button
            onClick={add}
            disabled={adding || !newName.trim()}
            className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#2f3a5d] disabled:opacity-50 text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
            data-testid="pc-category-add-btn"
          >
            {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Add
          </button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
            Active categories · {items.length}
          </div>
          {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
        </div>

        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">
            No categories yet. Add one above to begin.
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.id} className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50/50">
              <Tag size={14} className="text-[#1F7A6E] shrink-0" />
              {editingId === it.id ? (
                <>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(it); if (e.key === 'Escape') cancelEdit(); }}
                    autoFocus
                    className="flex-1 px-2 py-1.5 border border-[#1F7A6E] rounded-sm text-sm"
                    data-testid={`pc-category-edit-input-${it.id}`}
                  />
                  <button onClick={() => saveEdit(it)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Save"><Save size={14} /></button>
                  <button onClick={cancelEdit} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Cancel"><X size={14} /></button>
                </>
              ) : (
                <>
                  <div className="flex-1 text-sm font-display font-semibold">{it.name}</div>
                  <button onClick={() => startEdit(it)} className="p-1.5 text-slate-500 hover:text-[#1F7A6E] hover:bg-teal-50 rounded" title="Rename" data-testid={`pc-category-edit-${it.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(it)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded" title="Remove" data-testid={`pc-category-delete-${it.id}`}>
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
